/**
 * ProductScout Store
 *
 * MobX singleton managing client-side state for the ProductScout AI assistant
 * sidebar (wizard intake → streaming chat). Owns the active conversation, the
 * intake payload, message timeline, tool-call progress, usage budget readout,
 * and any user-facing error.
 *
 * Network round-trips go through `ProductScout.service.js`. In Slice 1 the
 * service returns mocked snake_case responses matching the documented API
 * surface (`docs/modules/ProductScout/api-reference.md`); Slice 2 swaps the
 * mocks for real HTTP. Streaming chat (Reverb tokens) is a Slice 3 concern —
 * for now `sendMessage` simulates a streamed reply via setInterval so the
 * UI's typing-bubble animation (PSCOUT-006) has something to render.
 *
 * Field naming preserves API snake_case verbatim per repo convention
 * (matches `LiveLeaderboardRow.av_id`, `FirstSaleTrackerStore.stats.pct_first_week`).
 *
 * Conventions matched against `src/pages/FirstSaleTracker/FirstSaleTracker.store.js`:
 *   - `class FooStore { … }` with field declarations + `@type` JSDoc.
 *   - `makeAutoObservable(this)` in the constructor.
 *   - `runInAction` wraps any mutation that follows an `await`.
 *   - Default export is a single `new FooStore()` instance (singleton).
 */

import { makeAutoObservable, runInAction } from 'mobx'
import ProductScoutService from '../../shared/services/ProductScout.service'
import EchoService from '../../shared/services/Echo.service'
import UserProfileService from '../../shared/services/UserProfile.service'

/**
 * Wizard intake payload schema. Mirrors the canonical shape from
 * `data-model.md` (`productscout_conversations.intake_payload`). All fields
 * are optional during entry; validation lives in `WizardPane` (PSCOUT-005).
 *
 * @typedef {Object} IntakePayload
 * @property {string} state              Two-letter US state code
 * @property {string} age_band           e.g. '18-24' | '25-34' | …
 * @property {string} gender             'male' | 'female' | 'other'
 * @property {boolean} tobacco           Tobacco user (true/false)
 * @property {string[]} conditions       Health condition slugs; empty = healthy
 * @property {string} household          Who's covered: individual|with_spouse|with_children|family ('' = not given)
 * @property {string} notes              Free-form notes (PII-screened)
 */

/**
 * Conversation envelope from POST /api/productscout/conversations.
 *
 * @typedef {Object} Conversation
 * @property {number} id
 * @property {'active'|'archived'|'blocked_by_budget'|'admin_terminated'} status
 * @property {string} started_at         ISO timestamp
 * @property {string|null} ended_at
 * @property {string|null} ended_reason
 * @property {IntakePayload} intake_payload
 * @property {string} flowchart_strategy
 * @property {string} system_prompt_version
 * @property {string} disclaimer_text
 */

/**
 * One row in the conversation's message timeline.
 *
 * @typedef {Object} Message
 * @property {number|string} id
 * @property {'system'|'user'|'assistant'|'tool'} role
 * @property {string} content
 * @property {Array<{tool:string,args:Object,result_summary?:string}>} [tool_calls]
 * @property {Array<{doc_id:number,page:number,excerpt:string}>} [citations]
 * @property {string} created_at
 * @property {string|null} [flagged_at]
 * @property {string|null} [flagged_reason]
 */

class ProductScoutStore {
  /** @type {Conversation|null} The active conversation, or null when in wizard mode. */
  current_conversation = null

  /** @type {Message[]} Message timeline for the active conversation. */
  messages = []

  /** @type {IntakePayload} Wizard intake draft state. */
  intake = {
    state: '',
    age_band: '',
    gender: '',
    tobacco: false,
    conditions: [],
    household: '',
    notes: '',
  }

  /** @type {boolean} True while the assistant is generating a reply (tokens
   * actively arriving in the latest assistant bubble). The compose input
   * is locked during streaming and the typing-dots indicator rides at the
   * end of the streaming bubble.
   */
  is_streaming = false

  /**
   * True from the moment a `sendMessage` is dispatched until the service
   * round-trip returns and the new assistant placeholder is appended. The
   * compose input is also locked during this brief window — distinct from
   * `is_streaming` so the typing-dots indicator does not attach to the
   * previous (already-settled) assistant bubble.
   * @type {boolean}
   */
  is_submitting = false

  /** @type {boolean} True while the intake-driven opening reply is generating. */
  is_opening = false

  /** @type {boolean} The opening reply failed; the chat shows its static welcome instead. */
  opening_failed = false

  /**
   * In-flight tool invocations the LLM has emitted but not yet completed.
   * Drives the UI's "looking up…" progress indicators.
   * @type {Array<{tool:string,args:Object,started_at:string}>}
   */
  pending_tool_calls = []

  /**
   * Per-user daily spend snapshot for the in-sidebar budget readout. `null`
   * before first fetch. Shape matches GET /api/productscout/usage.
   * @type {{today:Object, month_to_date:Object}|null}
   */
  usage_today = null

  /** @type {string|null} Last user-facing error message; `null` when healthy. */
  error = null

  /**
   * True from store construction until the component's mount-side async
   * setup (Echo subscription + resume of any active conversation) has
   * settled. Used to render a splash so the user doesn't see a wizard
   * flash that gets immediately replaced by the resumed chat.
   * @type {boolean}
   */
  is_initializing = true

  /**
   * G7: dedicated state for HTTP 429 with `error_code='PRODUCTSCOUT_BUDGET_BLOCKED'`.
   * Drives the chat compose-disabled banner. Distinct from `error` so a budget
   * block reads "you're over your limit — resets at X" rather than the generic
   * error-toast treatment that any other 4xx gets. `clearBudgetBlock()` resets.
   * @type {{scope:'daily'|'monthly', reset_at:string|null, message:string}|null}
   */
  budget_blocked = null

  /**
   * Cached list of the user's past conversations (most-recent first), populated
   * by `loadHistory()`.
   * @type {Array<{id:number,status:string,started_at:string,message_count:number,intake_summary:string}>}
   */
  history = []

  /**
   * Reverb channel name returned by `startConversation`. Slice 3 subscribes
   * to this for token streaming.
   * @type {string|null}
   */
  stream_channel = null

  /**
   * Internal handle for the streaming-chat mock's `setInterval`. Cleared on
   * unmount / new message so a previous run cannot keep firing into a stale
   * conversation.
   * @private
   * @type {number|null}
   */
  _stream_interval_id = null

  constructor() {
    makeAutoObservable(this, {
      _stream_interval_id: false, // not observable; raw mutable handle
    })
  }

  /**
   * True when there is an active conversation. Derived so the UI can branch
   * wizard vs. chat without touching the conversation object directly.
   * @returns {boolean}
   */
  get is_active() {
    return this.current_conversation !== null && this.current_conversation.status === 'active'
  }

  /**
   * Messages in chronological order (oldest → newest), keyed off the
   * server-assigned `id` which is auto-increment so it tracks insertion
   * order even when our local append order races.
   *
   * Why this matters: on a chat turn, the assistant's MessageDelta event
   * from Reverb can arrive BEFORE the HTTP response that triggered it
   * resolves. `_onDelta` appends the assistant placeholder, then the
   * HTTP handler appends the user message — producing a reversed pair.
   * Sorting by id at render time guarantees the UI always sees the
   * canonical chronological order regardless of which path mutated the
   * array first.
   *
   * @returns {Array<Message>}
   */
  get sortedMessages() {
    return this.messages.slice().sort((a, b) => {
      // Defensive: if either id is missing (in-flight placeholder before
      // Reverb assigns one), put it at the END (treat as newest).
      const aid = typeof a.id === 'number' ? a.id : Number.MAX_SAFE_INTEGER
      const bid = typeof b.id === 'number' ? b.id : Number.MAX_SAFE_INTEGER
      return aid - bid
    })
  }

  /**
   * Update a single field on the intake draft. Wizard inputs call this on
   * each change; submit packs the whole draft and calls `startConversation`.
   *
   * @param {keyof IntakePayload} field
   * @param {any} value
   */
  setIntakeField(field, value) {
    this.intake = { ...this.intake, [field]: value }
  }

  /**
   * Reset the intake draft to defaults. Called when the user clicks
   * "New Conversation" so the wizard starts blank.
   */
  resetIntake() {
    this.intake = {
      state: '',
      age_band: '',
      gender: '',
      tobacco: false,
      conditions: [],
      household: '',
      notes: '',
    }
  }

  /**
   * Start a new conversation from the current intake. Server-side enforces
   * one-active-per-user; the client mirrors that for predictable UX.
   *
   * @param {IntakePayload} [intake] Override the draft; defaults to `this.intake`.
   * @param {{open?: boolean}} [options] `open: false` skips the opening reply
   *   (the question path sends its own first message instead).
   * @returns {Promise<Conversation>}
   */
  async startConversation(intake, { open = true } = {}) {
    const payload = intake || this.intake
    runInAction(() => {
      this.error = null
      this.budget_blocked = null
    })

    const res = await ProductScoutService.startConversation(payload)
    if (this._handleServiceError(res)) return null

    runInAction(() => {
      this.current_conversation = res.conversation
      this.messages = res.initial_message ? [res.initial_message] : []
      this.is_streaming = false
      this.pending_tool_calls = []
      this.stream_channel = res.channel
      this.opening_failed = false
    })
    this._subscribeToChannel(res.channel)
    // Not awaited: the wizard unmounts as soon as the conversation exists
    // and the chat shows the opening reply's typing bubble meanwhile.
    if (open && res.opening_message) this.openConversation()
    return res.conversation
  }

  /**
   * Question path: start a conversation without the opening reply and send
   * the agent's question as the first message. `intake` may be partial or
   * empty.
   */
  async startWithQuestion(question, intake = {}) {
    const conv = await this.startConversation(intake, { open: false })
    if (!conv) return null
    await this.sendMessage(question)
    return conv
  }

  /**
   * Fetch the opening reply for a freshly started conversation. A failure
   * is soft: no error banner, the chat falls back to its static welcome.
   */
  async openConversation() {
    if (!this.current_conversation) return null
    const conversationId = this.current_conversation.id
    runInAction(() => {
      this.is_opening = true
      this.opening_failed = false
    })

    const res = await ProductScoutService.openConversation(conversationId)
    const status = parseInt(res?.status)
    const failed = !res || !!res.error || status >= 400

    runInAction(() => {
      this.is_opening = false
      if (this.current_conversation?.id !== conversationId) return
      if (failed) {
        // Budget blocks still surface (compose locks); anything else is soft.
        if (status === 429 && res?.data?.error_code === 'PRODUCTSCOUT_BUDGET_BLOCKED') this._handleServiceError(res)
        this.opening_failed = true
        return
      }
      const msg = res.assistant_message
      if (msg && !this.messages.some(m => m.id === msg.id)) {
        this.messages = [...this.messages, msg]
      } else if (msg) {
        this.messages = this.messages.map(m => (m.id === msg.id ? { ...m, ...msg } : m))
      }
      this.is_streaming = false
    })
    return res?.assistant_message || null
  }

  /**
   * Rehydrate the user's currently-active conversation (if one exists on
   * the backend) into the store so a page refresh doesn't strand the user
   * back in the wizard. No-op if there's already a conversation hydrated
   * or no active conversation exists on the backend.
   *
   * Failures are silent: this is a background "best effort" call that
   * fires on mount, and a stale token / network blip should not paint an
   * error banner before the user has done anything. The user can still
   * start fresh from the wizard.
   *
   * @returns {Promise<Conversation|null>} The hydrated conversation, or
   *   null when nothing was resumed.
   */
  async resumeActiveConversation() {
    if (this.current_conversation) return null

    const listRes = await ProductScoutService.listConversations({ per_page: 5 })
    if (this._handleServiceError(listRes)) {
      runInAction(() => {
        this.error = null
      })
      return null
    }
    const active = (listRes.data || []).find(c => c.status === 'active')
    if (!active) return null

    const showRes = await ProductScoutService.getConversation(active.id)
    if (this._handleServiceError(showRes)) {
      runInAction(() => {
        this.error = null
      })
      return null
    }

    runInAction(() => {
      this.current_conversation = showRes.conversation
      this.messages = Array.isArray(showRes.messages) ? showRes.messages : []
      this.is_streaming = false
      this.pending_tool_calls = []
      // Hydrate intake from the persisted payload so the empty-state hint
      // (and any future "edit intake" affordance) reads the prospect the
      // conversation was started for, not the empty default.
      if (showRes.conversation?.intake_payload) {
        this.intake = { ...this.intake, ...showRes.conversation.intake_payload }
      }
    })
    return showRes.conversation
  }

  /**
   * PSCOUT-026: subscribe to the conversation's private Reverb channel
   * so we receive MessageDelta / MessageCompleted / ToolInvoking /
   * ToolInvoked / CostUpdated events as the agent streams. The channel
   * name comes from the start-conversation response (private-prefix is
   * Pusher's wire convention; laravel-echo's `private()` adds it).
   */
  _subscribeToChannel(channelName) {
    if (!channelName) return
    const echo = EchoService.get && EchoService.get()
    if (!echo) {
      console.warn('[ProductScoutStore] Echo not initialized; live streaming disabled')
      return
    }
    // Strip the "private-" prefix - laravel-echo adds it back via private()
    const bare = String(channelName).replace(/^private-/, '')
    // G6: idempotent — re-mounts and ensureSubscribed() callbacks must not
    // tear down a healthy subscription. If we're already on the right
    // channel, leave it alone.
    if (this._channel_name === bare && this._channel) return
    this._unsubscribe()
    try {
      this._channel = echo
        .private(bare)
        .listen('.productscout.message.delta', e => this._onDelta(e))
        .listen('.productscout.message.completed', e => this._onCompleted(e))
        .listen('.productscout.tool.invoking', e => this._onToolInvoking(e))
        .listen('.productscout.tool.invoked', e => this._onToolInvoked(e))
        .listen('.productscout.cost.updated', e => this._onCostUpdated(e))
      this._channel_name = bare
    } catch (e) {
      console.warn('[ProductScoutStore] subscribe failed:', e?.message)
    }
  }

  /**
   * G6: subscribe eagerly to this user's private streaming channel so the
   * subscription survives page reloads, tab switches, and conversation
   * archives. Channel name is keyed by user_id (not conversation_id), so
   * one subscription serves every conversation the agent starts.
   *
   * Safe to call multiple times — internally idempotent via
   * `_subscribeToChannel`. Failure to initialize Echo (e.g. missing Reverb
   * env vars) degrades silently to HTTP-only mode; the warning is logged
   * by `Echo.service.js` and again here for context. Mount the component
   * once and forget — re-subscriptions are no-ops.
   *
   * @returns {Promise<void>}
   */
  async ensureSubscribed() {
    const userId = (UserProfileService.getUserId && UserProfileService.getUserId()) || null
    if (!userId) return
    try {
      await EchoService.init()
    } catch (e) {
      console.warn('[ProductScoutStore] Echo init failed; live streaming disabled:', e?.message)
      return
    }
    this._subscribeToChannel(`private-productscout.message.${userId}`)
  }

  /**
   * G7 branching error handler for service responses. The `RestApi`
   * response shape on non-2xx is `{ error, status, fields, data }`. We
   * discriminate on `data.error_code` so a budget-block 429 lights a
   * dedicated UI branch (`budget_blocked`), while every other failure
   * (validation, rate-limit, server error, network) sets the generic
   * `error` string.
   *
   * Replaces the dead `try / catch (e) { this.error = e.message }` blocks
   * that the old shape never reached (RestApi.responseHandler always
   * resolves, never rejects, so `catch` never ran).
   *
   * @param {Object} res Response from a `ProductScoutService.*` call.
   * @returns {boolean} true when an error was detected and state was
   *   updated; false when `res` looks like a success payload.
   */
  _handleServiceError(res) {
    if (!res) {
      runInAction(() => {
        this.error = 'Service returned no response.'
      })
      return true
    }
    const status = parseInt(res.status)
    const errorCode = res.data?.error_code
    const isError = !!(res.error || status >= 400)
    if (!isError) return false

    if (status === 429 && errorCode === 'PRODUCTSCOUT_BUDGET_BLOCKED') {
      runInAction(() => {
        this.budget_blocked = {
          scope: res.data?.scope || 'daily',
          reset_at: res.data?.reset_at || null,
          message: res.error || 'You have reached your ProductScout budget.',
        }
        this.error = null
      })
      return true
    }

    runInAction(() => {
      this.error = res.error || res.data?.error || 'Request failed.'
    })
    return true
  }

  /**
   * UI-facing reset for the budget-block banner. Called on dismiss or
   * when the agent confirms the reset time has passed.
   */
  clearBudgetBlock() {
    runInAction(() => {
      this.budget_blocked = null
    })
  }

  /**
   * UI-facing reset for the generic error message. Wizard + ChatPane
   * dismiss buttons call this so a single error toast does not stick
   * around forever after the user has seen it.
   */
  clearError() {
    runInAction(() => {
      this.error = null
    })
  }

  _unsubscribe() {
    if (!this._channel_name) return
    const echo = EchoService.get && EchoService.get()
    if (echo && echo.leave) echo.leave(this._channel_name)
    this._channel = null
    this._channel_name = null
  }

  /** Streaming-event handlers (PSCOUT-026). */
  _onDelta(e) {
    if (!e || !e.message_id) return
    runInAction(() => {
      const idx = this.messages.findIndex(m => m.id === e.message_id)
      if (idx === -1) {
        // First delta for a not-yet-seen message - insert a placeholder
        this.messages = [
          ...this.messages,
          {
            id: e.message_id,
            role: 'assistant',
            content: String(e.delta || ''),
            conversation_id: e.conversation_id,
          },
        ]
      } else {
        this.messages = this.messages.map(m =>
          m.id === e.message_id ? { ...m, content: (m.content || '') + String(e.delta || '') } : m
        )
      }
      this.is_streaming = true
    })
  }

  _onCompleted(e) {
    if (!e || !e.message_id) return
    runInAction(() => {
      this.messages = this.messages.map(m =>
        m.id === e.message_id
          ? {
              ...m,
              content: e.content ?? m.content,
              citations: e.citations ?? null,
              tool_calls: e.tool_calls ?? null,
            }
          : m
      )
      this.is_streaming = false
      this.pending_tool_calls = []
    })
  }

  _onToolInvoking(e) {
    if (!e || !e.tool) return
    runInAction(() => {
      this.pending_tool_calls = [
        ...this.pending_tool_calls.filter(t => t.tool !== e.tool),
        { tool: e.tool, args: e.args || {}, started_at: Date.now() },
      ]
    })
  }

  _onToolInvoked(e) {
    if (!e || !e.tool) return
    runInAction(() => {
      this.pending_tool_calls = this.pending_tool_calls.filter(t => t.tool !== e.tool)
    })
  }

  _onCostUpdated(e) {
    // Preserves the nested shape `refreshUsage()` populates so the UI's
    // `usage_today.today.spend_cents` accessor keeps working after a
    // Reverb update arrives (otherwise the live update would flatten the
    // structure and drop month_to_date).
    if (!e || !e.today) return
    runInAction(() => {
      this.usage_today = {
        today: { ...(this.usage_today?.today ?? {}), ...(e.today ?? {}) },
        month_to_date: {
          ...(this.usage_today?.month_to_date ?? {}),
          ...(e.month_to_date ?? {}),
        },
      }
    })
  }

  /**
   * Send a user message in the active conversation. Slice 1 simulates the
   * streamed assistant reply locally via setInterval so the UI's typing
   * bubble has something to render — the service returns a complete
   * assistant message, and the store chunks it for display. Slice 3 will
   * replace this with real Reverb token events.
   *
   * @param {string} content
   * @returns {Promise<Message|null>} The completed assistant message, or null on empty input.
   */
  async sendMessage(content) {
    if (!this.is_active) {
      throw new Error('No active conversation. Start one from the wizard.')
    }
    if (!content || !content.trim()) return null

    const conversationId = this.current_conversation.id
    // Show the agent's message right away; the reply can take a while.
    // Replaced by the server row when the response lands.
    const pendingId = `pending-${Date.now()}`

    runInAction(() => {
      this.is_submitting = true
      this.error = null
      this.messages = [
        ...this.messages,
        { id: pendingId, role: 'user', content: content.trim(), created_at: new Date().toISOString() },
      ]
    })

    const res = await ProductScoutService.sendMessage(conversationId, content.trim())

    if (this._handleServiceError(res)) {
      runInAction(() => {
        this.is_submitting = false
        this.messages = this.messages.filter(m => m.id !== pendingId)
      })
      return null
    }

    runInAction(() => {
      this.messages = this.messages.filter(m => m.id !== pendingId)
    })

    // PSCOUT-026: live streaming is now driven by the Reverb subscription
    // wired up in `_subscribeToChannel`. The HTTP response gives us the
    // canonical user/assistant message rows; the per-event handlers
    // (`_onDelta`, `_onCompleted`, etc.) update message content as the
    // backend streams. If events have already arrived by the time the
    // HTTP response lands (sync backend path), the handlers' findIndex
    // dedup logic keeps state consistent.
    const assistantFinal = res.assistant_message
    runInAction(() => {
      // User message: append once (no event for it - it's pre-flight only)
      const seenUser = this.messages.some(m => m.id === res.user_message.id)
      if (!seenUser) this.messages = [...this.messages, res.user_message]
      // Assistant placeholder: only if a delta hasn't already created it
      const seenAsst = this.messages.some(m => m.id === assistantFinal.id)
      if (!seenAsst) this.messages = [...this.messages, { ...assistantFinal, content: '' }]
      this.is_submitting = false
      this.is_streaming = true
    })

    // The streaming events will flip `is_streaming` back to false via
    // `_onCompleted`. If for some reason no completion event arrives
    // (e.g., Reverb down), we still resolve with the HTTP response's
    // assistant_message after a short grace period so the UI doesn't hang.
    return new Promise(resolve => {
      const start = Date.now()
      const check = () => {
        const msg = this.messages.find(m => m.id === assistantFinal.id)
        const complete = msg && msg.content && msg.content.length >= (assistantFinal.content?.length || 0)
        if (complete || !this.is_streaming || Date.now() - start > 30_000) {
          // ALWAYS clear is_streaming on exit, regardless of which branch
          // tripped us. The previous logic only flipped the flag in the
          // `!complete` branch — meaning when Reverb delivered the delta
          // (setting `complete=true`) but the completion event never
          // arrived, `is_streaming` stayed `true` and the UI's Send
          // button got stuck on "Thinking…" forever. The completion event
          // is the canonical signal, but the polling resolver is the
          // safety net; both should leave the flag in a consistent state.
          runInAction(() => {
            if (!complete) {
              // Reverb didn't land - finalize from the HTTP response.
              this.messages = this.messages.map(m => (m.id === assistantFinal.id ? assistantFinal : m))
            }
            this.is_streaming = false
          })
          resolve(assistantFinal)
          return
        }
        setTimeout(check, 100)
      }
      setTimeout(check, 50)
    })
  }

  /**
   * Archive the current conversation. Clears messages, resets intake, and
   * leaves the store in wizard mode for the next prospect. Idempotent.
   */
  async archiveConversation() {
    if (!this.current_conversation) return

    const res = await ProductScoutService.archive(this.current_conversation.id)
    if (this._handleServiceError(res)) return

    this._clearStream()
    // G6: channel is user-keyed (not conversation-keyed); do NOT unsubscribe.
    // The same subscription serves every conversation the agent starts.
    runInAction(() => {
      this.current_conversation = null
      this.messages = []
      this.is_streaming = false
      this.is_opening = false
      this.opening_failed = false
      this.pending_tool_calls = []
      this.stream_channel = null
    })
    this.resetIntake()
  }

  /**
   * Load the user's past conversations (read-only history dropdown).
   * Pagination deferred to a later ticket.
   */
  async loadHistory() {
    const res = await ProductScoutService.listConversations()
    if (this._handleServiceError(res)) return
    runInAction(() => {
      this.history = res.data
    })
  }

  /**
   * Refresh the daily + monthly usage readout shown in the sidebar footer.
   * Called on mount and after each `sendMessage` so the spend bar stays
   * current as tokens are consumed.
   */
  async refreshUsage() {
    const res = await ProductScoutService.getUsage()
    if (this._handleServiceError(res)) return
    runInAction(() => {
      this.usage_today = res
    })
  }

  /**
   * Flag an assistant message for engineering review. UI shows the action
   * inline on each assistant bubble.
   *
   * @param {Message['id']} message_id
   * @param {string} reason
   */
  async flagMessage(message_id, reason) {
    const res = await ProductScoutService.flagMessage(message_id, reason)
    if (this._handleServiceError(res)) return
    runInAction(() => {
      this.messages = this.messages.map(m =>
        m.id === message_id
          ? {
              ...m,
              flagged_at: res.flagged_at,
              flagged_reason: res.flagged_reason,
            }
          : m
      )
    })
  }

  /**
   * Stop the streaming-chat setInterval if running. Internal helper.
   * @private
   */
  _clearStream() {
    if (this._stream_interval_id !== null) {
      clearInterval(this._stream_interval_id)
      this._stream_interval_id = null
    }
  }
}

export default new ProductScoutStore()
